import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  CostCenterAssignment,
  FormattedAssignment,
  formatAssignments,
} from 'modules/finance/lib/costCenterAssignments'

import CostCenterPicker from './Picker'
import { DateSelectors } from './components/DateSelectors'
import { DeleteButton } from './components/DeleteButton'
import { UpdateButton } from './components/UpdateButton'
import { useAssignmentsManager } from './hooks/useAssignmentsManager'

interface Props {
  allAssignments?: CostCenterAssignment[]
  assignments?: CostCenterAssignment[]
  legalEntityIds: number[]
  resourceId: number
  title: string
  description: string
  customSaveButton?: (
    assignmentsToCreate: FormattedAssignment[],
    assignmentsToUpdate: FormattedAssignment[],
    disabled: boolean
  ) => React.ReactNode
  onCancel?: () => void
  onDelete?: () => void
  onSave?: () => void
}

export const ManageAssignments = ({
  allAssignments = [],
  assignments = [],
  legalEntityIds,
  resourceId,
  title,
  description,
  customSaveButton,
  onCancel,
  onDelete,
  onSave,
}: Props) => {
  const {
    assignmentsToCreate,
    assignmentsToUpdate,
    selectedDateRange,
    assignments: validAssignments,
    validForSave,
    validations,
    addNewAssignment,
    deleteValue,
    updateAssignment,
    setNewDateRange,
  } = useAssignmentsManager({
    currentAssignments: formatAssignments(assignments),
    employeeAssignments: allAssignments,
  })

  const assignmentsIds = assignments.map((assignment) => assignment.id)

  return (
    <Box>
      <Box paddingX="s24" paddingY="s24" gap="s24">
        <Box gap="s8">
          <Text.Normal weight="medium" size="300" color="grey1000">
            {title}
          </Text.Normal>

          <Text.Normal weight="normal" size="300" color="textSecondary">
            {description}
          </Text.Normal>
        </Box>

        <CostCenterPicker
          assignments={validAssignments}
          legalEntityIds={legalEntityIds}
          updateAssignment={updateAssignment}
          deleteValue={deleteValue}
          addNewAssignment={addNewAssignment}
        />

        <DateSelectors
          error={validations.overlappedAssignments}
          startDate={selectedDateRange.startDate}
          endDate={selectedDateRange.endDate}
          handleSelectedDatesChange={setNewDateRange}
          canBeMarkedAsActive={validations.canBeMarkedAsActive}
        />

        <Box flexDirection="row" gap="s8" paddingTop="s16" justifyContent="spaceBetween">
          {onDelete && <DeleteButton ids={assignmentsIds} onDelete={onDelete} />}

          <Box flexDirection="row" gap="s8" justifyContent="flexEnd" width="full">
            {onCancel && (
              <Button type="secondary" onClick={onCancel}>
                {i18n.t('common.cancel')}
              </Button>
            )}

            {customSaveButton ? (
              customSaveButton(assignmentsToCreate, assignmentsToUpdate, !validForSave)
            ) : (
              <UpdateButton
                assignmentsToCreate={assignmentsToCreate}
                assignmentsToUpdate={assignmentsToUpdate}
                resourceId={resourceId}
                onSave={() => {
                  track(trackingEvents.accounting.saveCostCenterAssignment, {
                    source: 'edit_assignment',
                  })

                  onSave?.()
                }}
                disabled={!validForSave}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
